import './loads.scss';
import {
    createRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ClbLoading } from '@celebration/react/ClbLoading/ClbLoading';
import { BreadcrumbItem } from '@celebration/react/ClbBreadcrumb/ClbBreadcrumb.types';
import Subheader from '../../../common/components/subheader';
import LoadCard from './components/load-card';
import LoadsConstants from './constants';
import HomeConstants from '../../home/constants';
import LoadManager from '../../../common/services/loadmanager';
import { useToast } from '../../../app/contexts/toast/toast.context';
import LoadsUtils from './utils';
import { LOAD_SELECTION_LIMIT } from './constants/loads.constants';
import ILoadListData from '../../../common/interfaces/loads/ILoadListData';
import ILoadsFiltersModel from '../../../common/interfaces/IFiltersModel';
import {
    selectCard,
    unselectCard,
} from '../../../common/utils/load-card-utils';
import LoadsFilters from '../../../common/components/loads-filters';

function Loads() {
    const { t } = useTranslation();
    const { setToast } = useToast();

    const page = useRef(0);
    const hasMore = useRef(true);
    const loadsContent = createRef<HTMLDivElement>();
    const numberOfSelectedCards = useRef(0);

    const [hasError, setHasError] = useState(false);
    const [loadList, setLoadList] = useState<ILoadListData[]>([]);
    const filters = useRef<ILoadsFiltersModel>({} as ILoadsFiltersModel);

    const breadcrumbList: BreadcrumbItem[] = [
        { label: HomeConstants.PAGE_TITLE, url: HomeConstants.PATH },
        { label: t(LoadsConstants.PAGE_TITLE), url: LoadsConstants.PATH },
    ];

    const loadListEndMessage = useMemo(() => {
        if (hasError) {
            return (
                <h2 data-testid="loads__fetch-loads-error-message">
                    {t('CargasErroConsultar')}
                </h2>
            );
        }
        if (loadList.length > 0) {
            return <h2>{t('CargasTodasJaExibidas')}</h2>;
        }

        return <h2>{t('CargasExibirNenhuma')}</h2>;
    }, [loadList.length, hasError, t]);

    const fetchData = useCallback(async () => {
        LoadManager.getLoads(filters.current, page.current).then(
            ({ response, error }) => {
                if (error !== undefined) {
                    hasMore.current = false;
                    setHasError(true);
                    setToast('negative', t('Erro'), error.message);
                    return;
                }

                hasMore.current = page.current < response!.totalPages;
                setLoadList(
                    response!.currentPage === 1
                        ? response!.data
                        : current => current.concat(response!.data),
                );
            },
        );
    }, [setToast, t]);

    const fetchNextPage = useCallback(async () => {
        page.current++;

        if (page.current === 1 || hasMore.current) {
            await fetchData();
        }
    }, [fetchData]);

    const fetchFirstPage = useCallback(async () => {
        page.current = 1;
        hasMore.current = true;
        setLoadList([]);
        fetchData();
    }, [fetchData]);

    const onSelectAllCards = useCallback(() => {
        const loadsToUpdate = [...loadList];
        const itemsAvailableToSelectCounter =
            LOAD_SELECTION_LIMIT - numberOfSelectedCards.current;
        let lastIndex = Math.min(
            loadsToUpdate.length,
            itemsAvailableToSelectCounter,
        );
        let i = 0;

        while (i < loadsToUpdate.length) {
            if (lastIndex < 1) {
                break;
            }

            if (loadsToUpdate[i].checked !== true) {
                loadsToUpdate[i].checked = true;
                numberOfSelectedCards.current += 1;
                lastIndex--;
            }

            i++;
        }

        setLoadList([...loadsToUpdate]);

        loadsContent.current
            ?.querySelectorAll('span[class = checkmark]')
            .forEach(x => x.classList.add('checkmark--checked'));
    }, [loadList, setLoadList, loadsContent, numberOfSelectedCards]);

    const onCleanCardSelection = useCallback(() => {
        const loadsToUpdate = [...loadList];
        loadsToUpdate.forEach(l => {
            l.checked = false;
        });
        setLoadList([...loadsToUpdate]);
        numberOfSelectedCards.current = 0;

        loadsContent.current
            ?.querySelectorAll('span[class = checkmark--checked]')
            .forEach(x => x.classList.remove('checkmark--checked'));
    }, [loadList, setLoadList, numberOfSelectedCards, loadsContent]);

    const onApplyFilters = useCallback(
        async (filtersModel: ILoadsFiltersModel) => {
            filters.current = filtersModel;
            await fetchFirstPage();
            onCleanCardSelection();
        },
        [fetchFirstPage, onCleanCardSelection],
    );

    const handleCardSelected = useCallback(
        (loadId: string) => {
            selectCard({
                numberOfSelectedCards,
                selectionLimit: LOAD_SELECTION_LIMIT,
                loadId,
                setLoadList,
                setToast,
            });
        },
        [setLoadList, numberOfSelectedCards, setToast],
    );

    const handleCardUnselected = useCallback(
        (loadId: string) => {
            unselectCard({ numberOfSelectedCards, loadId, setLoadList });
        },
        [setLoadList, numberOfSelectedCards],
    );

    const updateItem = useCallback(
        (load: ILoadListData) => {
            setLoadList(prevItems =>
                prevItems.map((item: ILoadListData) =>
                    item.loadId === load.loadId
                        ? {
                              ...item,
                              status: load.status,
                              statusReason: load.statusReason,
                              isPrioritized: load.isPrioritized,
                              channel: load.channel,
                              dates: load.dates,
                              isScorecardEnabled: load.isScorecardEnabled,
                              route: load.route,
                              shipment: load.shipment,
                              shipmentNumber: load.shipmentNumber,
                              sapOrderNumber: load.sapOrderNumber,
                              vehicle: load.vehicle,
                              winnerInfo: load.winnerInfo,
                              confirmedBy: load.confirmedBy,
                              type: load.type,
                              rates: load.rates,
                              rate: load.rate,
                              isPool: load.isPool,
                              isPocFreightAuction: load.isPocFreightAuction,
                              isReturnable: load.isReturnable,
                          }
                        : item,
                ),
            );
        },
        [setLoadList],
    );

    useEffect(() => {
        if (page.current === 0) {
            fetchFirstPage();
        }
    }, [fetchFirstPage]);

    return (
        <div className="loads-grid-page">
            <Subheader
                title={t(LoadsConstants.PAGE_TITLE_CARD)}
                breadcrumbList={breadcrumbList}
                dataTestid="loads-subheader"
            />

            <LoadsFilters
                filters={filters}
                selectedCards={loadList
                    .filter(x => x.checked)
                    .map(x => LoadsUtils.fillLoadInfo(x))}
                onApplyFilters={onApplyFilters}
                onSelectAllCards={onSelectAllCards}
                onCleanCardSelection={onCleanCardSelection}
                fetchFirstPage={fetchFirstPage}
            />

            <div
                id="loads-content"
                className="loads-content"
                ref={loadsContent}
                data-testid="loads-content-scrollable"
            >
                <InfiniteScroll
                    className="loads-list"
                    dataLength={loadList.length}
                    next={fetchNextPage}
                    hasMore={hasMore.current}
                    loader={<ClbLoading />}
                    endMessage={loadListEndMessage}
                    data-testid="infinite-scroll"
                    scrollableTarget="loads-content"
                >
                    {loadList.length > 0 &&
                        loadList.map((load: ILoadListData) => {
                            const data = LoadsUtils.fillLoadInfo(load);
                            return (
                                <LoadCard
                                    key={`${load.loadId}`}
                                    numberOfSelectedCards={
                                        loadList.filter(x => x.checked).length
                                    }
                                    loadInfo={data}
                                    handleCardSelected={handleCardSelected}
                                    handleCardUnselected={handleCardUnselected}
                                    reloadMainPage={fetchFirstPage}
                                    updateItem={updateItem}
                                />
                            );
                        })}
                </InfiniteScroll>
            </div>
        </div>
    );
}

export default Loads;
